import React, { ReactNode } from "react"

export type MessageEntity = {
  type:
    | "bold"
    | "italic"
    | "strikethrough"
    | "spoiler"
    | "text_link"
    | "underline"
    | "code"
  offset: number
  length: number
  url?: string
}

/**
 * Renders a Telegram text string with MessageEntity[] annotations as a
 * React node tree. Each entity maps to the appropriate HTML tag / className.
 *
 * Telegram measures entity offset/length in UTF-16 code units (emoji outside
 * the BMP count as 2 units), so we index/slice the string directly via
 * String.prototype.slice, which operates on UTF-16 code units.
 */
export function renderEntities(
  text: string,
  entities: MessageEntity[],
): ReactNode {
  if (!entities || entities.length === 0) return text

  const sorted = [...entities].sort((a, b) => a.offset - b.offset)
  const nodes: ReactNode[] = []
  let pos = 0

  const wrapEntity = (
    segment: string,
    entity: MessageEntity,
    key: number,
  ): ReactNode => {
    switch (entity.type) {
      case "bold":
        return <b key={key}>{segment}</b>
      case "italic":
        return <i key={key}>{segment}</i>
      case "strikethrough":
        return <s key={key}>{segment}</s>
      case "underline":
        return <u key={key}>{segment}</u>
      case "code":
        return (
          <code key={key} className="tg-code">
            {segment}
          </code>
        )
      case "spoiler":
        return (
          <span
            key={key}
            className="tg-spoiler"
            onClick={(e) =>
              (e.currentTarget as HTMLElement).classList.toggle(
                "tg-spoiler--revealed",
              )
            }
          >
            {segment}
          </span>
        )
      case "text_link":
        return (
          <a
            key={key}
            href={entity.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {segment}
          </a>
        )
      default:
        return segment
    }
  }

  for (const entity of sorted) {
    if (pos < entity.offset) {
      nodes.push(text.slice(pos, entity.offset))
    }
    const entityText = text.slice(entity.offset, entity.offset + entity.length)
    nodes.push(wrapEntity(entityText, entity, entity.offset))
    pos = entity.offset + entity.length
  }

  if (pos < text.length) {
    nodes.push(text.slice(pos))
  }

  return <>{nodes}</>
}

/**
 * HTML-string version of renderEntities — used in unit tests to avoid
 * JSX serialization complexity. Produces the same logical output.
 */
export function renderEntitiesToHtml(
  text: string,
  entities: MessageEntity[],
): string {
  if (!entities || entities.length === 0) return text

  const sorted = [...entities].sort((a, b) => a.offset - b.offset)
  let result = ""
  let pos = 0

  const wrap = (segment: string, entity: MessageEntity): string => {
    switch (entity.type) {
      case "bold":
        return `<b>${segment}</b>`
      case "italic":
        return `<i>${segment}</i>`
      case "strikethrough":
        return `<s>${segment}</s>`
      case "underline":
        return `<u>${segment}</u>`
      case "code":
        return `<code class="tg-code">${segment}</code>`
      case "spoiler":
        return `<span class="tg-spoiler">${segment}</span>`
      case "text_link":
        return `<a href="${entity.url}" target="_blank" rel="noopener noreferrer">${segment}</a>`
      default:
        return segment
    }
  }

  for (const entity of sorted) {
    if (pos < entity.offset) {
      result += text.slice(pos, entity.offset)
    }
    const entityText = text.slice(entity.offset, entity.offset + entity.length)
    result += wrap(entityText, entity)
    pos = entity.offset + entity.length
  }

  if (pos < text.length) {
    result += text.slice(pos)
  }

  return result
}
